import type { Phone, ResidenceSectionV2 } from "@credit-acceptance/step6-bff-client";

import {
  APPLICANT_SSN_TYPES,
  type ApplicantSsnType,
  MAX_PHONES_PER_PERSON,
  RESIDENCE_OWNER_TYPE_DEFAULT,
  RESIDING_SINCE_MONTH_DEFAULT,
} from "./constants";
import { type CustomerInfoInput, makeEmptyCustomerInfo, makeEmptyPhone, type PhoneInput } from "./Signers.schema";

// ---------------------------------------------------------------------------
// residenceMapping
//
// Maps the BFF `ResidenceSectionV2` (ApplicantSectionV2.residence) onto the
// form's `CustomerInfoInput` (personal details, physical/mailing address, and
// contact). Pure function — no React — so it stays unit-testable and reusable
// for both the Driver and 2nd Signer columns.
//
// SSN/ITIN: `ssnOrItin` (resolved from Customer Hub, step6-bff-client@1.0.287)
// hydrates the form's editable SSN/ITIN input as canonical digits. It is null
// when unavailable / no record / `isNoItinSsn`, in which case the field stays
// blank. The `isSsn` / `isNoItinSsn` flags drive `deriveApplicantSsnType`,
// which gates the Income Type list and clamps hydrated income types.
// ---------------------------------------------------------------------------

/**
 * Derives the applicant's SSN/ITIN status from the residence flags. Precedence:
 * `isNoItinSsn` (no valid SSN/ITIN on file) wins; otherwise `isSsn === true` is
 * an SSN; every other case (ITIN, or an unavailable/unknown status) falls to
 * "itin", matching dealer-ui's `isSsn ? "ssn" : "itin"` fallback. Non-SSN
 * statuses share the same restricted Income Type list.
 * @param {ResidenceSectionV2 | undefined} residence - The applicant's BFF residence section.
 * @returns {ApplicantSsnType} The applicant's SSN/ITIN status.
 */
export function deriveApplicantSsnType(residence?: ResidenceSectionV2): ApplicantSsnType {
  if (residence?.isNoItinSsn) {
    return APPLICANT_SSN_TYPES.NO_ITIN_SSN;
  }
  if (residence?.isSsn === true) {
    return APPLICANT_SSN_TYPES.SSN;
  }
  return APPLICANT_SSN_TYPES.ITIN;
}

/**
 * Splits the BFF `Address.residingSince` into the form's short-month / 4-digit
 * year pair. Tolerates the shapes the BFF has been observed to carry —
 * `YYYY-MM-DD`, `YYYY-MM`, and `MM/YYYY` — and falls back to the legacy
 * defaults (January, blank year) for an absent or unparseable value, so the
 * dropdown never hydrates blank.
 * @param {string | undefined} residingSince - The BFF residing-since value.
 * @returns {{ month: string; year: string }} The form's month ("01".."12") and year ("" or 4 digits).
 */
export function splitResidingSince(residingSince?: string): { month: string; year: string } {
  const fallback = { month: RESIDING_SINCE_MONTH_DEFAULT, year: "" };
  const value = (residingSince ?? "").trim();
  if (value === "") {
    return fallback;
  }

  const isoMatch = /^(\d{4})-(\d{2})(?:-\d{2})?$/.exec(value);
  const slashMatch = /^(\d{2})\/(\d{4})$/.exec(value);
  const year = isoMatch?.[1] ?? slashMatch?.[2];
  const month = isoMatch?.[2] ?? slashMatch?.[1];
  if (!year || !month) {
    return fallback;
  }

  // A month outside 01-12 isn't selectable, so keep the default rather than
  // surfacing an out-of-options value in the dropdown.
  const monthNumber = Number(month);
  if (monthNumber < 1 || monthNumber > 12) {
    return { month: RESIDING_SINCE_MONTH_DEFAULT, year };
  }
  return { month, year };
}

/**
 * Maps a BFF phone onto the form's phone row. Only WORK (usage type 2) maps to
 * "Work"; every other usage type maps to "Personal" (the form's two options).
 * @param {Phone} phone - A BFF phone record.
 * @returns {PhoneInput} The hydrated phone row.
 */
function mapPhone(phone: Phone): PhoneInput {
  return {
    id: phone.id ?? crypto.randomUUID(),
    number: phone.number ?? "",
    type: phone.phoneUsageTypeId === 2 ? "Work" : "Personal",
    extension: phone.extension ?? "",
  };
}

/**
 * Maps the applicant's `ResidenceSectionV2` onto the form's customer
 * information. Reads the physical/mailing addresses directly off `current`/
 * `mailing`, orders phones/emails by `displayOrder`, and falls back to form
 * defaults for fields the BFF does not carry. Returns blank customer info
 * when the residence is absent.
 * @param {ResidenceSectionV2 | undefined} residence - The applicant's BFF residence section.
 * @returns {CustomerInfoInput} The hydrated customer information.
 */
export function mapResidenceSectionToCustomerInfo(residence?: ResidenceSectionV2): CustomerInfoInput {
  const base = makeEmptyCustomerInfo();
  if (!residence) {
    return base;
  }

  const physical = residence.current;
  const mailing = residence.mailing;

  // Residence details describe the CURRENT address. `landlords` carries the
  // same owner type / residing-since pair, so fall back to it when the
  // address slot itself hasn't been populated (OR-25320).
  const landlord = physical?.landlords;
  const residenceOwnerTypeId = physical?.residenceOwnerTypeId ?? landlord?.residenceOwnerTypeId;
  const { month: residingSinceMonth, year: residingSinceYear } = splitResidingSince(
    physical?.residingSince ?? landlord?.residingSince
  );

  const phones = [...(residence.phones ?? [])]
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    .slice(0, MAX_PHONES_PER_PERSON)
    .map((phone) => mapPhone(phone));

  // Always show Primary + Secondary rows, padding with blanks if the BFF
  // returned fewer than two phones.
  while (phones.length < 2) {
    phones.push(makeEmptyPhone());
  }

  // Polarity flip: the BFF carries `isEmailAvailable`, the form carries the
  // inverse `emailNotAvailable`. The write side flips it back in
  // `Signers.putMapping.ts` → `mapCustomerInfoToEmailsSection`.
  // Explicit `=== false` (not `!residence.isEmailAvailable`) so an unanswered
  // flag (undefined/null on a not-yet-touched applicant) still defaults to
  // unchecked rather than being conflated with an explicit "no email".
  const emailNotAvailable = residence.isEmailAvailable === false;

  // Blank the email whenever the flag says there is none. Hydrating a checked
  // box next to a stale address is a state the UI can't reach interactively
  // (the checkbox handler clears email), and the stale value would be silently
  // re-saved on the next PUT.
  const emailAddress = emailNotAvailable
    ? ""
    : ([...(residence.emailAddresses ?? [])].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))[0]
        ?.emailAddress ?? "");

  return {
    ...base,
    // Canonical digits only; SsnInput also strips, but keep the model clean.
    ssnOrItin: residence.ssnOrItin?.replaceAll(/\D/g, "") ?? "",
    firstName: residence.firstName ?? "",
    middleInitial: residence.middleName?.charAt(0) ?? "",
    lastName: residence.lastName ?? "",
    suffix: residence.nameSuffix ?? "",
    dateOfBirth: residence.dateOfBirth ?? "",
    physicalStreetNumber: physical?.streetNumber ?? "",
    physicalStreetName: physical?.streetName ?? "",
    physicalApartmentNumber: physical?.apartmentNumber ?? "",
    physicalZip: physical?.zip ?? "",
    physicalCity: physical?.city ?? "",
    physicalState: physical?.state ?? "",
    physicalAddressId: physical?.addressId,
    physicalAddressUspsStandardType: physical?.addressUspsStandardType,
    // Owner type ids are 1-4, so a falsy value (absent/null/0) is never a real
    // selection and falls back to the legacy default.
    residenceOwnerType: residenceOwnerTypeId ? String(residenceOwnerTypeId) : RESIDENCE_OWNER_TYPE_DEFAULT,
    residingSinceMonth,
    residingSinceYear,
    physicalLandlord: landlord,
    // Always hydrates unchecked — this is purely a user-driven UI toggle
    // (see useMirrorAddress), not derived from whether current/mailing happen
    // to match on the BFF. Mailing's own field values below still hydrate
    // from whatever the BFF has on file.
    mailingSameAsCurrent: false,
    mailingStreetNumber: mailing?.streetNumber ?? "",
    mailingStreetName: mailing?.streetName ?? "",
    mailingApartmentNumber: mailing?.apartmentNumber ?? "",
    mailingZip: mailing?.zip ?? "",
    mailingCity: mailing?.city ?? "",
    mailingState: mailing?.state ?? "",
    mailingAddressId: mailing?.addressId,
    mailingAddressUspsStandardType: mailing?.addressUspsStandardType,
    phones,
    emailAddress,
    emailNotAvailable,
  };
}
